/* eslint-disable @typescript-eslint/no-explicit-any */
import * as rax from 'retry-axios';
import axios, { AxiosRequestConfig, Method } from 'axios';
import { authState } from '@/services/integrations/auth0';
import { KeyValuePair } from '@/types/Global';
import SiemLogging from '@/services/siem-logging';

interface Request {
  method: Method;
  path: string;
  data?: Record<string, any>;
}

axios.defaults.withCredentials = true;

class ServerAPI {
  serverUrl(): string {
    let serverUrl: string = 'https://api.' + window.location.host;

    if (window.location.host.includes('localhost')) {
      serverUrl = 'http://localhost:8000';
    }

    return serverUrl;
  }

  async get(path: string, data?: Record<string, any>) {
    return (await this.makeRequest({ method: 'GET', path, data })).data;
  }

  async post(path: string, data?: Record<string, any>) {
    return (await this.makeRequest({ method: 'POST', path, data })).data;
  }

  async delete(path: string) {
    return (await this.makeRequest({ method: 'DELETE', path })).data;
  }

  async patch(path: string, data: Record<string, any>) {
    return (await this.makeRequest({ method: 'PATCH', path, data })).data;
  }

  async put(path: string, data?: Record<string, any>) {
    return (await this.makeRequest({ method: 'PUT', path, data })).data;
  }

  private async makeRequest(request: Request) {
    rax.attach();

    const serverUrl = this.serverUrl();

    const headers: AxiosRequestConfig['headers'] = {};

    if (authState.bearerToken) {
      headers.Authorization = `Bearer ${authState.bearerToken}`;
    }

    // axios >=v0.25.0: there is now error handling if the url is having issues
    // if there is an unhandled error here, please note this.
    const res = await axios({
      url: serverUrl + request.path,
      method: request.method,
      data: request.data,
      headers,
      raxConfig: {
        retry: 6,                   // retry-axios v4 breaking update 3+3
        onError: async (err) => {
          console.error('Error occurred, will retry after backoff');
        },
        onRetryAttempt: async (err) => {      // retry-axios v4 breaking update to async
          const cfg = rax.getConfig(err);
          console.error('Retryable request failed', {
            message: err.message,
            status: err.response?.status,
            method: err.config?.method,
            url: err.config?.url,
            retryAttempt: cfg?.currentRetryAttempt,
          });
          console.error(`Retry attempt #${cfg ? cfg.currentRetryAttempt : ''}`); // eslint-disable-line no-console
        },
      },
    });
    return res;
  }
}
export default new ServerAPI();

const arrayToQueryParam = (
  key: string,
  array: Array<KeyValuePair>,
  firstInQuery: boolean
) => {
  let paramsString = '';

  array.forEach((item, i) => {
    if (item.key !== '' && item.value !== null && item.value !== undefined) {
      if (firstInQuery && !i) {
        paramsString += `?${key}=${item.key}:${item.value}`;
      } else {
        paramsString += `&${key}=${item.key}:${item.value}`;
      }
    }
  });
  return paramsString;
};

export const combineQueryParams = (paramObj: {
  [key: string]:
    | string
    | number
    | boolean
    | Array<KeyValuePair>
    | Array<string>
    | undefined;
}) => {
  const paramKeys = Object.keys(paramObj);

  let paramsList = '';

  paramKeys.forEach(paramKey => {
    if (
      paramObj[paramKey] !== '' &&
      paramObj[paramKey] !== null &&
      paramObj[paramKey] !== undefined
    ) {
      if (Array.isArray(paramObj[paramKey])) {
        paramsList += arrayToQueryParam(
          paramKey,
          paramObj[paramKey] as Array<KeyValuePair>,
          paramsList.length === 0
        );
      } else if (paramsList.length === 0) {
        paramsList += `?${paramKey}=${paramObj[paramKey]}`;
      } else {
        paramsList += `&${paramKey}=${paramObj[paramKey]}`;
      }
    }
  });

  return paramsList;
};

const handleSiemLogging = (e: any) => {
  if (e.response && e.response.status === 400) {
    SiemLogging.error({
      ['event_name']: 'Input Validation Failure',
      message: e.response.data.detail,
    });
  }

  if (e.response && e.response.status === 422) {
    SiemLogging.error({
      ['event_name']: 'Output Validation Failure',
      message: e.response.data.detail,
    });
  }
};

export const handleError = (e: any) => {
  let message = 'Unknown error';

  handleSiemLogging(e);

  if (e.response) {
    const response = e.response.data;
    if (e.response.status === 404) {
      return;
    } else if (response.detail && Array.isArray(response.detail)) {
      message = response.detail.map((error: any) => error.msg);
    } else if (response.detail && !Array.isArray(response.detail)) {
      message = response.detail;
    } else if (response.message) {
      message = response.message;
    }
  }

  alert(message);
  console.error(e); // eslint-disable-line no-console
  return;
};
